import { useEffect, useState } from "react";
import { tokens } from "@/art/tokens";
import { getHouseScenarioEndings, type HouseScenarioEndingItem } from "@/api/endpoints/houseEndings";
import { useSession } from "@/state/session";

/**
 * 같은 하우스가 같은 시나리오를 이미 완주한 적 있다면, RunForgeScene 혹은 시나리오 프리뷰
 * 자리에 "이전 원정 기록" 으로 노출해주는 callout. 처음 돌리는 런이거나 해당 시나리오를
 * 아직 끝낸 기록이 없으면 렌더하지 않음.
 *
 * <p>현재는 단순 표시 — 향후 prior ending 기반 시작 보너스/수정자가 들어가면 같은 data 를
 * gameplay 쪽에서도 참조하면 된다.
 */
export interface PriorEndingsCalloutProps {
    scenarioId: string;
    /** endingId → 플레이어가 알아볼 수 있는 엔딩 이름 (안 주면 endingId 그대로). */
    endingLabelMap?: Record<string, string>;
}

export function PriorEndingsCallout({ scenarioId, endingLabelMap = {} }: PriorEndingsCalloutProps) {
    const [items, setItems] = useState<HouseScenarioEndingItem[] | null>(null);
    const [failed, setFailed] = useState(false);
    const houseUuid = useSession(s => s.identity?.houseUuid ?? null);

    useEffect(() => {
        // 인증 정보 없으면 fetch 자체를 건너뜀 — 예전엔 401 이 콘솔에 찍혀 소음이었음.
        if (!houseUuid) {
            setItems([]);
            return;
        }
        let cancelled = false;
        setItems(null);
        setFailed(false);
        getHouseScenarioEndings(scenarioId)
            .then(list => { if (!cancelled) setItems(list); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [scenarioId, houseUuid]);

    if (failed || !items || items.length === 0) return null;

    return (
        <div
            className="rounded-l p-m mb-m relative overflow-hidden"
            style={{
                background: `radial-gradient(circle at 0% 0%, ${tokens.color.brass}12 0%, transparent 55%), linear-gradient(180deg, ${tokens.color.ink}F0, ${tokens.color.ink}C8)`,
                border: `1px solid ${tokens.color.brass}55`,
                boxShadow: `0 18px 38px ${tokens.color.ink}88, inset 0 1px 0 ${tokens.color.brass}22`,
            }}
        >
            {/* 2026-04-23: 좌측 brass accent — Spirytale 언어 통일. */}
            <div
                aria-hidden
                className="absolute inset-y-0 left-0"
                style={{
                    width: 3,
                    background: `linear-gradient(180deg, ${tokens.color.brass}FF 0%, ${tokens.color.brass}44 100%)`,
                    boxShadow: `0 0 10px ${tokens.color.brass}AA`,
                }}
            />
            {/* 2026-04-25: "연대기 · 이 하우스의 장부" kicker + 설명문 "이 하우스가 이미 지나온..." 제거.
                타이틀 "이전 원정의 기록" 만으로 자명. */}
            <div
                className="font-display mb-s pl-[6px]"
                style={{
                    color: tokens.color.parchment,
                    fontSize: "clamp(22px, 1.5vw, 28px)",
                    letterSpacing: "0.04em",
                    textShadow: `0 0 18px ${tokens.color.brass}44, 0 1px 0 ${tokens.color.ink}`,
                }}
            >
                이전 원정의 기록
            </div>
            <div className="flex flex-col gap-xs pl-[6px]">
                {items.slice(0, 5).map(item => (
                    <div
                        key={item.runId}
                        className="flex items-center justify-between gap-s rounded-m px-s py-[6px]"
                        style={{
                            background: `linear-gradient(180deg, ${tokens.color.parchment}10, ${tokens.color.parchment}06)`,
                            border: `1px solid ${tokens.color.brass}33`,
                            boxShadow: `inset 0 1px 0 ${tokens.color.brass}11, 0 10px 20px ${tokens.color.ink}22`,
                        }}
                    >
                        <div
                            className="font-body"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 16.5,
                                letterSpacing: "0.03em",
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                            {item.characterName}
                        </div>
                        <div
                            className="font-display rounded-full px-s py-[4px]"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 15,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                background: `${tokens.color.brass}18`,
                                border: `1px solid ${tokens.color.brass}55`,
                                boxShadow: `0 0 14px ${tokens.color.brass}22`,
                            }}
                        >
                            {endingLabelMap[item.endingId] ?? item.endingId} · {gradeLabel(item.outcomeGrade)}
                        </div>
                    </div>
                ))}
            </div>
            {items.length > 5 && (
                <div
                    className="mt-xs pl-[6px] font-body"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 14.5,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    +{items.length - 5} 건의 이전 기록
                </div>
            )}
        </div>
    );
}

function gradeLabel(grade: string): string {
    switch (grade) {
        case "LEGEND": return "전설";
        case "SUCCESS": return "성공";
        case "MODEST": return "참가";
        case "FALTERED": return "좌절";
        default: return grade;
    }
}

/** frontier-vanguard 전용 endingId → 한글 라벨. RunForgeScene 에서 사용. */
export const FRONTIER_VANGUARD_ENDING_LABELS: Record<string, string> = {
    "frontier-conqueror": "변경의 정복자",
    "border-mediator": "경계의 중재자",
    "returned-vanguard": "돌아온 선봉",
    "hollow-return": "텅 빈 귀환",
    "buried-beyond-line": "선 밖에 묻힌 이름",
};
